import { safeChineseMessage } from '../../utils/errorMessage';

// Keep the business error code for both HTTP failures and HTTP 200 error envelopes.
export function paymentResponseError(body) {
  const error = new Error(typeof body?.message === 'string' ? body.message : '');
  error.response = { data: body };
  return error;
}

export function getPaymentErrorCode(error) {
  return error?.response?.data?.data?.error_code || error?.errorCode || error?.code || '';
}

export function paymentErrorMessage(error, english = false, operation = 'create') {
  const code = getPaymentErrorCode(error);
  const body = error?.response?.data;
  const status = Number(body?.code || error?.response?.status);
  const message = typeof body?.message === 'string' ? body.message.trim()
    : typeof error?.message === 'string' ? error.message.trim() : '';
  // Only show concise business copy, never transport errors or implementation details.
  const technical = /配置|密钥|证书|堆栈|数据库|私钥|公钥|签名|request failed|status code|network error|timeout|failed to fetch|exception|SQLSTATE|SQL|stack|trace|undefined|null|TypeError|SyntaxError|[<>\r\n{}]|https?:\/\/|[A-Z][A-Z0-9]+_[A-Z0-9_]+/i;
  const businessMessage = !(status >= 500) && message.length <= 160
    && safeChineseMessage(message) && !technical.test(message) ? message : '';
  // Error guidance is always Chinese; retain the language argument for callers.
  const fallback = operation === 'query'
    ? '支付结果暂时无法确认，请稍后再次查询，或前往「账单记录」查看。若已扣款，请勿重复付款。'
    : operation === 'cancel'
      ? '暂时无法确认是否取消成功，请稍后重试或前往「账单记录」查看订单状态。若已扣款，请勿重复付款。'
      : '暂时无法发起支付，请稍后重试。如已操作付款，请先到「账单记录」核对状态，避免重复付款；仍有问题请联系客服。';

  if (code === 'PAYMENT_CHANNEL_MISMATCH') {
    return '这笔订单已使用其他支付方式发起，请前往「账单记录」找到待支付订单，点击「继续支付」使用原支付方式付款。若已扣款，请先查询支付结果，勿重复付款。';
  }
  if (code === 'PACKAGE_CHANGED' || code === 'SUBSCRIPTION_CHANGED') {
    return '套餐信息已更新，请刷新套餐列表并重新确认价格和权益后再支付。';
  }
  if (code === 'ORDER_EXPIRED' || code === 'ORDER_CLOSED') {
    return '订单已过期或关闭，请先到「账单记录」核对支付状态，确认未付款后重新下单。';
  }
  if (['PAYMENT_PENDING', 'PAYMENT_RECONCILING', 'PAYMENT_RESULT_PENDING', 'ORDER_CANCELLATION_PENDING', 'BILLING_BUSY'].includes(code)) {
    return '订单正在处理中，支付或取消结果尚未确认。请稍后到「账单记录」查询，请勿重复付款。';
  }
  if (code === 'PENDING_SUBSCRIPTION_EXISTS') {
    return '你还有一笔待支付的订阅订单，请先前往「账单记录」查看并处理，再购买新的订阅。';
  }
  if (['ORDER_ALREADY_PAID', 'ORDER_STATUS_CHANGED', 'PAYMENT_CHANNEL_CHANGED', 'ORDER_NOT_PAYABLE'].includes(code)) {
    return '订单状态已更新，请到「账单记录」刷新查看最新结果，再按订单提示操作；若已扣款，请勿重复付款。';
  }
  if (status === 401) return '登录状态已过期，请重新登录后查看订单并继续支付。';
  if (status === 403) return '当前账户暂时无法进行此支付操作，请确认账户或团队权限，或联系管理员。';
  if (status === 429) return '操作有些频繁，请稍等片刻再试。若已付款，请先到「账单记录」查询结果，勿重复付款。';

  // A failed lookup or cancellation never establishes whether funds moved.
  if (operation === 'query' || operation === 'cancel') {
    return businessMessage ? `${businessMessage.replace(/[。；]$/, '')}。${fallback}` : fallback;
  }
  if (/^(ALIPAY|WECHAT|WECHAT_PAY|UNIONPAY|UNIONPAY_B2B)_.*(CONFIG|DISABLED|UNAVAILABLE|NOT_ENABLED|NOT_CONFIGURED)/.test(code)) {
    const channel = code.startsWith('ALIPAY') ? '支付宝'
      : code.startsWith('WECHAT') ? '微信支付' : '银联支付';
    return `${channel}暂时无法使用，请稍后再试或联系客服。若已发起过支付，请先到「账单记录」核对订单状态。`;
  }
  if (['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(code) || /network error|timeout|failed to fetch/i.test(error?.message || '')) {
    return '网络连接不稳定，暂时未能确认支付请求结果。请检查网络，并先到「账单记录」核对订单状态；若已扣款，请勿重复付款。';
  }
  if (status >= 500) return fallback;

  if (businessMessage) {
    return /请|稍后|重试|重新|联系|查看|核对/.test(message)
      ? message
      : `${message.replace(/[。；]$/, '')}。请稍后重试；若已付款，请先到「账单记录」核对状态。`;
  }
  return fallback;
}
